import { fmtDate } from '../utils/format';
import { logActivity } from '../utils/activityLog';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Check, X, Plus, Trash2, ChevronDown,
    RefreshCw,
  FileText, Upload, Pin, MoreVertical, Phone, CalendarDays, Users
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { userSettingsService } from '../services/userSettings';
import { toast } from 'sonner';
import { STATUS_PALETTE } from './Settings';
import {
  getCurrentUser,
  hasPermission,
  canEditSales,
  canEditRetention,
  canDeleteComment,
  canEditComment,
  canUseTransactionAdminControls,
  roleDepartment,
  SALES_ROLES,
  RET_ROLES,
} from '../utils/roles';
import UploadPreviewModal from '../components/UploadPreviewModal';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtMoney = (n) => (n ?? 0).toFixed(2) + ' $';

const normalizeDialNumber = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === '-') return '';
  const hasPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return hasPlus ? `+${digits}` : digits;
};

const openLocalSipDialer = (number) => {
  window.location.href = `sip:${encodeURIComponent(number)}`;
};

const applyDialPrefix = (number, prefix) => {
  const cleanNumber = String(number || '').trim();
  const cleanPrefix = String(prefix || '').trim();
  if (!cleanPrefix || cleanNumber.startsWith(cleanPrefix)) return cleanNumber;
  return `${cleanPrefix}${cleanNumber}`;
};

// statusColorMap is built at runtime from /options/statuses; fallback to gray
let _statusColorMap = {};
export const loadStatusColors = async () => {
  try {
    const { data } = await api.get('/options/statuses', { params: { entity_type: 'all' } });
    _statusColorMap = Object.fromEntries((data.statuses || []).map(s => [s.name, STATUS_PALETTE[s.color] || STATUS_PALETTE.gray]));
  } catch { /* ignore */ }
};
const statusColor = (s) => _statusColorMap[s] || STATUS_PALETTE.gray;

// ─── combined name field (display: "First Last", edit: two inputs) ───────────
function NameField({ firstName, lastName, onSave, isDark }) {
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(firstName ?? '');
  const [last, setLast] = useState(lastName ?? '');

  const open = () => { setFirst(firstName ?? ''); setLast(lastName ?? ''); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = async () => {
    if (first !== (firstName ?? '')) await onSave('firstName', first);
    if (last !== (lastName ?? ''))   await onSave('lastName', last);
    setEditing(false);
  };

  const labelCls = `text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
  const valCls   = `text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`;
  const inputCls = `text-sm border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 w-full ${
    isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  if (!editing) {
    return (
      <div className="flex items-start gap-1 min-w-0">
        <div className="flex-1 min-w-0">
          <p className={labelCls}>Name:</p>
          <p className={valCls}>{[firstName, lastName].filter(Boolean).join(' ') || '—'}</p>
        </div>
        <button onClick={open} className={`mt-4 ${isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-600'}`}>
          <Pencil size={13} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1 min-w-0 col-span-2">
      <div className="flex-1 min-w-0">
        <p className={labelCls}>Name:</p>
        <div className="flex gap-2 mt-0.5">
          <div className="flex-1">
            <p className={`text-xs mb-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>First</p>
            <input value={first} onChange={e => setFirst(e.target.value)} className={inputCls} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }} />
          </div>
          <div className="flex-1">
            <p className={`text-xs mb-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Last</p>
            <input value={last} onChange={e => setLast(e.target.value)} className={inputCls}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }} />
          </div>
        </div>
      </div>
      <div className="flex gap-1 mt-7">
        <button onClick={save} className="text-green-500 hover:text-green-400"><Check size={14} /></button>
        <button onClick={cancel} className="text-red-500 hover:text-red-400"><X size={14} /></button>
      </div>
    </div>
  );
}

// ─── dept status card — purely informational, active dept highlighted ──────────
function DeptStatusCard({ deptLabel, deptColor, accentBorder, accentBg, statuses, agent, desk, team, isDark, isActive, t }) {
  const subColor = isDark ? 'rgba(255,255,255,0.28)' : '#94a3b8';
  const labelColor = isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8';
  const agentColor = isDark ? 'rgba(255,255,255,0.65)' : '#334155';
  return (
    <div
      className={`rounded-xl border p-3 mb-2 last:mb-0 transition-all ${
        isActive
          ? `${accentBorder}`
          : (isDark ? 'border-gray-800/40' : 'border-gray-100')
      }`}
      style={{ opacity: isActive ? 1 : 0.45 }}
    >
      {/* Two-column layout */}
      <div className="flex items-start gap-3">
        {/* LEFT — dept label + status */}
        <div className="flex-1 min-w-0">
          <span className={`text-[10px] font-bold tracking-[0.18em] uppercase ${deptColor}`}>{deptLabel}</span>
          <div className="mt-1 flex flex-wrap gap-1">
            {statuses.length ? statuses.map(status => (
              <span key={status} className={`rounded px-2 py-0.5 text-xs font-semibold ${_statusColorMap[status] || STATUS_PALETTE.gray}`}>
                {status}
              </span>
            )) : <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>—</span>}
          </div>
        </div>

        {/* RIGHT — agent / desk / team (read-only reference) */}
        <div className="shrink-0 text-right space-y-0.5">
          <p className="text-xs font-semibold truncate" style={{ color: agentColor }}>
            {agent || <span style={{ color: isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1' }}>{t('clientDetail.notAssigned')}</span>}
          </p>
          <p className="text-[11px] truncate" style={{ color: subColor }}>
            <span style={{ color: labelColor }}>{t('clientDetail.desk')}: </span>{desk || '—'}
          </p>
          <p className="text-[11px] truncate" style={{ color: subColor }}>
            <span style={{ color: labelColor }}>{t('clientDetail.team')}: </span>{team || '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── change status modal — dept driven by user role ───────────────────────────
function ChangeStatusModal({ client, statusOptions, isDark, onSave, onClose, userRole }) {
  const userDept    = roleDepartment(userRole);           // 'sales' | 'retention' | null
  const clientDept  = (client.department || 'sales').toLowerCase();
  const isPrivileged = canEditSales(userRole) && canEditRetention(userRole);

  // default tab: privileged → client's dept; dept user → their own dept
  const [targetDept, setTargetDept] = useState(
    isPrivileged ? (clientDept === 'retention' ? 'retention' : 'sales') : userDept
  );
  const [selected, setSelected] = useState([]);
  const [saving, setSaving]     = useState(false);

  // helpers — same fallback used in the status card
  const currentSalesStatus = client.salesStatus || (clientDept !== 'retention' ? client.status : '') || '';
  const currentRetStatus   = client.retStatus   || (clientDept === 'retention'  ? client.status : '') || '';
  const currentSalesStatuses = client.salesStatuses?.length ? client.salesStatuses : (currentSalesStatus ? [currentSalesStatus] : []);
  const currentRetStatuses = client.retStatuses?.length ? client.retStatuses : (currentRetStatus ? [currentRetStatus] : []);

  useEffect(() => {
    setSelected(targetDept === 'retention' ? currentRetStatuses : currentSalesStatuses);
  }, [targetDept, client]);

  const deptOptions = statusOptions.filter(s =>
    (s.entityType || 'sales').toLowerCase() === targetDept
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await onSave(targetDept === 'retention' ? 'retStatuses' : 'salesStatuses', selected);
      if (saved !== false) onClose();
    } finally {
      setSaving(false);
    }
  };

  const deptCfg = {
    sales:     { color: '#60a5fa', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.35)' },
    retention: { color: '#22d3ee', bg: 'rgba(6,182,212,0.15)',  border: 'rgba(6,182,212,0.35)'  },
  };
  const cfg = deptCfg[targetDept];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl animate-scale-in"
        style={{
          background: isDark ? 'rgba(8,12,22,0.97)' : '#fff',
          border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(99,102,241,0.18)',
          boxShadow: isDark ? '0 32px 80px rgba(0,0,0,0.7)' : '0 32px 80px rgba(99,102,241,0.15)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(99,102,241,0.1)' }}>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
              Change Status
            </h2>
            {/* Show dept badge in header — always visible, not just when privileged */}
            <span
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ color: cfg.color }}
            >
              {targetDept}
            </span>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}
            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <X size={15} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Dept tabs — only for privileged roles */}
          {isPrivileged && (
            <div className="flex gap-2">
              {['sales', 'retention'].map(d => {
                const dc = deptCfg[d];
                const isActive = targetDept === d;
                return (
                  <button key={d} onClick={() => setTargetDept(d)}
                    className="flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
                    style={isActive ? {
                      background: dc.bg, color: dc.color, border: `1px solid ${dc.border}`,
                    } : {
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                      color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8',
                      border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)',
                    }}
                  >
                    {d}
                    {clientDept === d && (
                      <span className="w-1.5 h-1.5 rounded-full" style={{
                        background: dc.color, boxShadow: `0 0 5px ${dc.color}`,
                        animation: 'pulse-glow 2s ease-in-out infinite',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Current status */}
          <div className="text-xs flex items-center gap-2" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
            Current:
            <span className="font-semibold" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
              {(targetDept === 'retention' ? currentRetStatuses : currentSalesStatuses).join(', ') || '—'}
            </span>
          </div>

          {/* Status pills */}
          {deptOptions.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8' }}>
              No statuses configured
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-0.5">
              {deptOptions.map(s => {
                const sName = typeof s === 'object' ? s.name : s;
                const isSelected = selected.includes(sName);
                return (
                  <button key={sName} onClick={() => setSelected(values => values.includes(sName) ? values.filter(value => value !== sName) : [...values, sName])}
                    className="px-3 py-2 rounded-xl text-xs font-semibold text-left transition-all"
                    style={isSelected ? {
                      background: 'rgba(99,102,241,0.2)',
                      border: '1px solid rgba(99,102,241,0.45)',
                      color: '#a5b4fc',
                      boxShadow: '0 0 10px rgba(99,102,241,0.15)',
                    } : {
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
                      color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
                    }}>
                    {sName}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b',
              border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)',
            }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || deptOptions.length === 0}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 0 16px rgba(99,102,241,0.3)' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── transfer client to another agent (Sales / Retention) ─────────────────────
// Reuses the same /clients/bulk/assign path the Clients list uses, scoped to
// this one client. Picking a Sales agent moves the client to Sales, a Retention
// agent moves it to Retention (department is derived server-side from the agent).
function TransferAgentModal({ client, clientId, users, statusOptions, isDark, userRole, onClose, onTransferred }) {
  const userDept     = roleDepartment(userRole);          // 'sales' | 'retention' | null
  const isPrivileged = userDept === null;                 // owner/admin/crm_manager pick the dept
  const clientDept   = (client.department || 'sales').toLowerCase();

  const [dept, setDept]       = useState(isPrivileged ? (clientDept === 'retention' ? 'retention' : 'sales') : userDept);
  const [search, setSearch]   = useState('');
  const [agentId, setAgentId] = useState('');
  const [status, setStatus]   = useState('');            // optional dept status to set after transfer
  const [saving, setSaving]   = useState(false);

  const agentDept = (u) => {
    const d = (u.department || '').toLowerCase();
    if (d === 'sales' || d === 'retention') return d;
    if (SALES_ROLES.includes(u.role)) return 'sales';
    if (RET_ROLES.includes(u.role))   return 'retention';
    return '';
  };

  useEffect(() => { setAgentId(''); setStatus(''); }, [dept]);

  const options = users
    .filter(u => agentDept(u) === dept)
    .filter(u => !search || (u.name || '').toLowerCase().includes(search.trim().toLowerCase()));

  // Status options for the target department (sales_status vs ret_status)
  const deptStatuses = (statusOptions || []).filter(s => (s.entityType || 'sales').toLowerCase() === dept);

  const currentAgent = dept === 'retention'
    ? (client.retAgent   || (clientDept === 'retention' ? client.agent : ''))
    : (client.salesAgent || (clientDept !== 'retention' ? client.agent : ''));

  const handleSave = async () => {
    if (!agentId) { toast.error('Select an agent'); return; }
    setSaving(true);
    try {
      await api.post('/clients/bulk/assign', { client_ids: [Number(clientId)], value: agentId });
      // If a status was chosen, set the dept-specific status (sales_status / ret_status)
      if (status) {
        const field = dept === 'retention' ? 'retStatus' : 'salesStatus';
        await api.patch(`/clients/${clientId}/field`, { field, value: status });
      }
      const agent = users.find(u => String(u.id) === String(agentId));
      toast.success(`Transferred to ${agent?.name || 'agent'}`);
      onTransferred && onTransferred();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  const deptCfg = {
    sales:     { color: '#60a5fa', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.35)' },
    retention: { color: '#22d3ee', bg: 'rgba(6,182,212,0.15)',  border: 'rgba(6,182,212,0.35)'  },
  };
  const inputCls = `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <Modal title="Transfer to agent" onClose={onClose} isDark={isDark}>
      <div className="space-y-4">
        {isPrivileged && (
          <div className="flex gap-2">
            {['sales', 'retention'].map(d => {
              const dc = deptCfg[d];
              const isActive = dept === d;
              return (
                <button key={d} onClick={() => setDept(d)}
                  className="flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                  style={isActive
                    ? { background: dc.bg, color: dc.color, border: `1px solid ${dc.border}` }
                    : { background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)' }}>
                  {d}
                </button>
              );
            })}
          </div>
        )}

        <div className="text-xs flex items-center gap-2" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
          Current {dept} agent:
          <span className="font-semibold" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>{currentAgent || '—'}</span>
        </div>

        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agent…" className={inputCls} autoFocus />

        <div className={`max-h-56 overflow-y-auto rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {options.length === 0 ? (
            <p className="text-xs text-center py-6" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>No agents found</p>
          ) : options.map(u => {
            const isSel = String(agentId) === String(u.id);
            return (
              <button key={u.id} onClick={() => setAgentId(u.id)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${isSel ? 'bg-purple-500/20' : isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-50'}`}>
                <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>{u.name}</span>
                <span className="text-[10px] uppercase tracking-wide flex-shrink-0" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
                  {[u.desk, u.team].filter(Boolean).join(' · ') || u.role}
                </span>
              </button>
            );
          })}
        </div>

        {/* Optional status for the target department */}
        {deptStatuses.length > 0 && (
          <div>
            <label className="block text-xs mb-1" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
              Status{' '}
              <span className="font-semibold" style={{ color: deptCfg[dept].color }}>
                ({dept === 'retention' ? 'Retention' : 'Sales'})
              </span>{' '}
              <span style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#cbd5e1' }}>(optional)</span>
            </label>
            <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
              <option value="">— Keep current status —</option>
              {deptStatuses.map(s => {
                const n = typeof s === 'object' ? s.name : s;
                return <option key={n} value={n}>{n}</option>;
              })}
            </select>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium ${isDark ? 'bg-white/[0.05] text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !agentId}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            {saving ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── inline-edit field ───────────────────────────────────────────────────────
// groupedOptions: [{label, items: [{name, entityType}]}]
function EditableField({ label, value, field, onSave, isDark, options, groupedOptions }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');

  const save = async () => {
    if (val === (value ?? '')) { setEditing(false); return; }
    await onSave(field, val);
    setEditing(false);
  };

  const labelCls = `text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
  const valCls   = `text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`;
  const inputCls = `text-sm border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
    isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div className="flex items-start gap-1 min-w-0">
      <div className="flex-1 min-w-0">
        <p className={labelCls}>{label}:</p>
        {editing ? (
          groupedOptions ? (
            <select value={val} onChange={e => setVal(e.target.value)} className={inputCls} autoFocus>
              <option value="">—</option>
              {groupedOptions.map(grp => (
                <optgroup key={grp.label} label={grp.label}>
                  {grp.items.map(o => {
                    const n = typeof o === 'object' ? o.name : o;
                    return <option key={n} value={n}>{n}</option>;
                  })}
                </optgroup>
              ))}
            </select>
          ) : options ? (
            <select value={val} onChange={e => setVal(e.target.value)} className={inputCls} autoFocus>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input value={val} onChange={e => setVal(e.target.value)} className={inputCls} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
          )
        ) : (
          <p className={valCls}>{value || '—'}</p>
        )}
      </div>
      {editing ? (
        <div className="flex gap-1 mt-4">
          <button onClick={save} className="text-green-500 hover:text-green-400"><Check size={14} /></button>
          <button onClick={() => { setVal(value ?? ''); setEditing(false); }} className="text-red-500 hover:text-red-400"><X size={14} /></button>
        </div>
      ) : (
        <button onClick={() => { setVal(value ?? ''); setEditing(true); }}
          className={`mt-4 ${isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-600'}`}>
          <Pencil size={13} />
        </button>
      )}
    </div>
  );
}

// ─── tab button ──────────────────────────────────────────────────────────────
function Tab({ label, active, onClick, isDark }) {
  return (
    <button onClick={onClick} className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active
        ? 'border-purple-500 text-purple-400'
        : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
    }`}>{label}</button>
  );
}

// ─── DateTimePicker ───────────────────────────────────────────────────────────
const DTP_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DTP_DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];
const DTP_HOURS  = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const DTP_MINS   = ['00','05','10','15','20','25','30','35','40','45','50','55'];

function dtpGrid(year, month) {
  const first = new Date(year, month, 1);
  let dow = first.getDay(); // 0=Sun
  dow = dow === 0 ? 6 : dow - 1; // Mon=0
  const days = [];
  for (let i = dow - 1; i >= 0; i--) days.push(new Date(year, month, -i));
  const last = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= last; d++) days.push(new Date(year, month, d));
  while (days.length < 42) {
    const prev = days[days.length - 1];
    days.push(new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1));
  }
  return days;
}

function DateTimePicker({ date, time, onChangeDT, isDark, placeholder = 'Select date & time' }) {
  const [open, setOpen]         = useState(false);
  const [step, setStep]         = useState('date'); // 'date' | 'time'
  const [vy, setVy]             = useState(() => date ? parseInt(date.slice(0,4)) : new Date().getFullYear());
  const [vm, setVm]             = useState(() => date ? parseInt(date.slice(5,7)) - 1 : new Date().getMonth());
  const [selHour, setSelHour]   = useState(() => time ? time.slice(0,2) : null);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const prevMonth = () => { if (vm === 0) { setVm(11); setVy(y => y - 1); } else setVm(m => m - 1); };
  const nextMonth = () => { if (vm === 11) { setVm(0); setVy(y => y + 1); } else setVm(m => m + 1); };

  const pickDay = (d) => {
    const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    onChangeDT(iso, time || '');
    setSelHour(null);
    setStep('time');
  };

  const pickTime = (h, m) => {
    const t = `${h}:${m}`;
    onChangeDT(date, t);
    setOpen(false);
    setStep('date');
  };

  const today = new Date();
  const grid  = dtpGrid(vy, vm);

  const displayVal = () => {
    if (!date) return '';
    const [y, mo, d] = date.split('-');
    const monthName = DTP_MONTHS[parseInt(mo)-1]?.slice(0,3);
    const t = time ? ` ${time}` : '';
    return `${d} ${monthName} ${y}${t}`;
  };

  const bg    = isDark ? 'bg-[#0f1729] border-gray-700' : 'bg-white border-gray-200';
  const bgHov = isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100';
  const txt   = isDark ? 'text-white' : 'text-gray-900';
  const sub   = isDark ? 'text-gray-500' : 'text-gray-400';
  const inCls = `w-full px-3 py-2 border rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <div className="relative" ref={wrapRef}>
      {/* Trigger input */}
      <div
        className={inCls + (date ? '' : ` ${sub}`)}
        onClick={() => { setOpen(o => !o); setStep('date'); }}
        style={{ userSelect: 'none' }}
      >
        {displayVal() || <span className={sub}>{placeholder}</span>}
      </div>

      {/* Popup */}
      {open && (
        <div className={`absolute z-50 mt-1 rounded-xl border shadow-2xl ${bg} overflow-hidden`} style={{ minWidth: 280 }}>

          {/* ── DATE STEP ── */}
          {step === 'date' && (
            <div className="p-3">
              {/* Month nav */}
              <div className="flex items-center justify-between mb-3">
                <button onClick={prevMonth} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="rotate-90" /></button>
                <span className={`text-sm font-semibold ${txt}`}>{DTP_MONTHS[vm]} {vy}</span>
                <button onClick={nextMonth} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="-rotate-90" /></button>
              </div>
              {/* Day names */}
              <div className="grid grid-cols-7 mb-1">
                {DTP_DAYS.map(d => (
                  <div key={d} className={`text-center text-xs font-medium py-1 ${sub}`}>{d}</div>
                ))}
              </div>
              {/* Day grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {grid.map((d, i) => {
                  const inMonth  = d.getMonth() === vm;
                  const isToday  = d.toDateString() === today.toDateString();
                  const selected = date === `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                  return (
                    <button
                      key={i}
                      onClick={() => pickDay(d)}
                      className={`
                        w-8 h-8 mx-auto flex items-center justify-center rounded-full text-xs transition-colors
                        ${!inMonth ? 'opacity-25' : ''}
                        ${selected ? 'bg-purple-600 text-white font-bold' : isToday ? `ring-2 ring-purple-500 ${txt} ${bgHov}` : `${txt} ${bgHov}`}
                      `}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── TIME STEP ── */}
          {step === 'time' && (
            <div className="p-3">
              <div className="flex items-center gap-2 mb-3">
                <button onClick={() => setStep('date')} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="rotate-90" /></button>
                <span className={`text-sm font-semibold ${txt}`}>
                  {date ? (() => { const [y,mo,d] = date.split('-'); return `${d} ${DTP_MONTHS[parseInt(mo)-1]?.slice(0,3)} ${y}`; })() : ''}
                </span>
                <span className={`ml-auto text-xs ${sub}`}>pick time</span>
              </div>

              {!selHour ? (
                /* Hour grid */
                <div>
                  <p className={`text-xs mb-2 ${sub}`}>Hour</p>
                  <div className="grid grid-cols-6 gap-1">
                    {DTP_HOURS.map(h => (
                      <button
                        key={h}
                        onClick={() => setSelHour(h)}
                        className={`py-1.5 rounded text-xs font-mono transition-colors ${
                          time?.slice(0,2) === h
                            ? 'bg-purple-600 text-white'
                            : `${txt} ${bgHov}`
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                /* Minute grid */
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <button onClick={() => setSelHour(null)} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="rotate-90" /></button>
                    <p className={`text-xs ${sub}`}>{selHour}:__ — pick minute</p>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {DTP_MINS.map(m => (
                      <button
                        key={m}
                        onClick={() => pickTime(selHour, m)}
                        className={`py-1.5 rounded text-xs font-mono transition-colors ${
                          time === `${selHour}:${m}`
                            ? 'bg-purple-600 text-white'
                            : `${txt} ${bgHov}`
                        }`}
                      >
                        :{m}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── modal wrapper ────────────────────────────────────────────────────────────
function Modal({ title, onClose, isDark, children, maxW = 'max-w-md' }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className={`w-full ${maxW} rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
          <button onClick={onClose} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}><X size={20} /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ─── standalone helpers ───────────────────────────────────────────────────────
const calcAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
};

const timeAgoStr = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d !== 1 ? 's' : ''} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo !== 1 ? 's' : ''} ago`;
  const yr = Math.floor(mo / 12);
  return `${yr} year${yr !== 1 ? 's' : ''} ago`;
};

const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', hour12: false });
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';

  const [client, setClient]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState('comments');
  const defaultDocumentTitleRef = useRef(document.title || 'Abramov CRM');

  useEffect(() => {
    const clientName = [client?.firstName, client?.lastName].filter(Boolean).join(' ').trim();
    document.title = clientName || `Client #${id}`;
  }, [client?.firstName, client?.lastName, id]);

  useEffect(() => () => {
    document.title = defaultDocumentTitleRef.current;
  }, []);

  // Comments
  const [comments, setComments]     = useState([]);
  const [showNewComment, setShowNewComment] = useState(false);
  const [newComment, setNewComment] = useState({ body: '', pinned: false, status: '', next_call_date: '', next_call_time: '' });
  const [statusOptions, setStatusOptions] = useState([]);

  // Edit comment modal
  const [editingComment, setEditingComment] = useState(null); // comment object being edited
  const [editCommentForm, setEditCommentForm] = useState({ title: '', body: '', pinned: false, status: '', next_call_date: '', next_call_time: '' });

  // Transactions (per-client)
  const [clientTxns, setClientTxns] = useState([]);
  const [showNewTxn, setShowNewTxn] = useState(false);
  const [newTxn, setNewTxn] = useState({ amount: '', type: 'deposit', currency: 'USD', status: 'completed', description: '' });

  // Change status modal
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [callDialOptions, setCallDialOptions] = useState([{ name: 'No prefix', prefix: '' }]);
  const [showCallMenu, setShowCallMenu] = useState(false);
  const [callMenuPos, setCallMenuPos] = useState({ top: 0, left: 0 });

  const fieldsConfigRef = useRef(null);
  const callMenuRef = useRef(null);
  const callButtonRef = useRef(null);

  // Action modals
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferUsers, setTransferUsers] = useState([]);

  // Pinned comments
  const [pinnedComments, setPinnedComments] = useState([]);

  // Configurable Personal Information fields visibility
  const PI_FIELDS = [
    'Date of birth', 'Gender', 'Phone', 'Second Phone', 'Email', 'Second Email', 'Country', 'Citizenship', 'Language',
    'City', 'Address', 'Affiliate', 'Affiliate ID', 'Source', 'Referral', 'Funnel', 'Reg. IP',
  ];
  const [showFieldsConfig, setShowFieldsConfig] = useState(false);
  const [visibleFields, setVisibleFields] = useState(() => new Set(PI_FIELDS));
  useEffect(() => {
    let cancelled = false;
    userSettingsService.get('clientDetail.visibleFields', null).then(value => {
      if (!cancelled && Array.isArray(value)) setVisibleFields(new Set(value));
    });
    return () => { cancelled = true; };
  }, []);
  const toggleFieldVisibility = (f) => {
    setVisibleFields(prev => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      userSettingsService.set('clientDetail.visibleFields', [...next]).catch(() => {});
      return next;
    });
  };

  // Documents sub-tab
  const [docSubTab, setDocSubTab] = useState('verification');
  const DOC_TYPES = ['Passport', 'Driver License', 'ID Front', 'ID Back', 'Utility Bill', 'Bank Statement'];
  const [clientDocs, setClientDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docFileRef = useRef(null);
  const invoiceFileRef = useRef(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const filesFileRef = useRef(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const cls = {
    card: `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input: `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
    label: `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    btn: `px-4 py-2 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50',
  };


  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    logActivity('view', 'client', id, `Opened client #${id}`);
    loadStatusColors();
    loadClient();
    loadCallDialOptions();
    api.get('/options/statuses').then(({ data }) => setStatusOptions(data.statuses || [])).catch(() => {});
    api.get(`/clients/${id}/comments`).then(({ data }) => {
      setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab === 'comments')      loadComments();
    if (activeTab === 'transactions')  loadClientTxns();
    if (activeTab === 'documents')     loadClientDocs();
    logActivity('view', `client_tab_${activeTab}`, id, `Viewed tab "${activeTab}" on client #${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id]);

  useEffect(() => {
    if (!showFieldsConfig) return;
    const h = (e) => { if (fieldsConfigRef.current && !fieldsConfigRef.current.contains(e.target)) setShowFieldsConfig(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showFieldsConfig]);

  useEffect(() => {
    if (!showCallMenu) return;
    const h = (e) => {
      const inMenu = callMenuRef.current?.contains(e.target);
      const inButton = callButtonRef.current?.contains(e.target);
      if (!inMenu && !inButton) setShowCallMenu(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showCallMenu]);

  const loadClient = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch (error) {
      setClient(null);
      setLoadError(error.response?.status === 404 ? 'clientNotFound' : 'loadFailed');
    }
    finally { setLoading(false); }
  };

  const loadComments = async () => { try { const { data } = await api.get(`/clients/${id}/comments`); setComments(data); } catch {} };
  const loadClientTxns = async () => { try { const { data } = await api.get('/transactions', { params: { client_id: id, limit: 100 } }); setClientTxns(data.transactions || []); } catch {} };
  const loadCallDialOptions = async () => {
    try {
      const { data } = await api.get('/telephony/dial-options');
      const options = Array.isArray(data?.options) && data.options.length
        ? data.options
        : [{ name: 'No prefix', label: 'MicroSIP - No prefix', prefix: '' }];
      setCallDialOptions(options);
    } catch {
      setCallDialOptions([{ name: 'No prefix', label: 'MicroSIP - No prefix', prefix: '' }]);
    }
  };

  const startTelephonyCall = (dialOption = null) => {
    const number = normalizeDialNumber(client?.phone || client?.phone2 || client?.mobile);
    if (!number) {
      toast.error('Client phone number is missing');
      return;
    }
    setShowCallMenu(false);
    const dialNumber = applyDialPrefix(number, dialOption?.prefix);
    openLocalSipDialer(dialNumber);
    toast.success(`Opening MicroSIP: ${dialNumber}`);
  };

  const toggleCallMenu = () => {
    if (callDialOptions.length === 1) {
      startTelephonyCall(callDialOptions[0]);
      return;
    }
    const rect = callButtonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 180;
      setCallMenuPos({
        top: Math.min(rect.bottom + 6, window.innerHeight - 12),
        left: Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12)),
      });
    }
    setShowCallMenu(v => !v);
  };


  const loadClientDocs = async () => {
    try {
      setDocsLoading(true);
      const { data } = await api.get('/documents', { params: { client_id: id, limit: 100 } });
      setClientDocs(data.documents || []);
    } catch {} finally { setDocsLoading(false); }
  };
  const handleDocUpload = async (file, docType) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploadingDoc(true);
      const { data } = await api.post('/documents/upload', fd, {
        params: { client_id: id, doc_type: docType },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const isVerification = DOC_TYPES.includes(docType);
      toast.success(isVerification
        ? `${docType} загружен, одобрен и опубликован клиенту`
        : `${docType} загружен — проверьте превью`);
      loadClientDocs();
      // Don't open preview modal for verification slot uploads — already approved+published
      if (data?.id && !isVerification) setPreviewDoc(data);
    } catch { toast.error('Upload failed'); } finally { setUploadingDoc(false); setUploadDocType(null); }
  };
  const changeDocStatus = async (docId, newStatus) => {
    try {
      await api.patch(`/documents/${docId}/status`, { status: newStatus });
      toast.success(newStatus === 'declined' ? 'Document declined and deleted' : `Status -> ${newStatus}`);
      loadClientDocs();
    } catch { toast.error('Failed'); }
  };
  const deleteClientDoc = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Deleted');
      loadClientDocs();
    } catch { toast.error('Delete failed'); }
  };
  const handleInvoiceUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploadingInvoice(true);
      const { data } = await api.post('/documents/upload', fd, {
        params: { client_id: id, doc_type: 'invoice' },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Инвойс загружен — проверьте превью');
      loadClientDocs();
      if (data?.id) setPreviewDoc(data);
    } catch { toast.error('Upload failed'); } finally { setUploadingInvoice(false); }
  };
  const handleFilesUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploadingFiles(true);
      const { data } = await api.post('/documents/upload', fd, {
        params: { client_id: id, doc_type: 'file' },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Файл загружен — проверьте превью');
      loadClientDocs();
      if (data?.id) setPreviewDoc(data);
    } catch { toast.error('Upload failed'); } finally { setUploadingFiles(false); }
  };

  // ── Field save ─────────────────────────────────────────────────────────────
  const handleFieldSave = async (field, value, silent = false) => {
    try {
      await api.patch(`/clients/${id}/field`, { field, value });
      setClient(c => {
        const next = { ...c, [field]: value };
        const activeStatus = (c.department || '').toLowerCase() === 'retention' ? 'retStatus' : 'salesStatus';
        const activeStatuses = `${activeStatus}es`;
        if (field === activeStatus) next.status = value;
        if (field === 'salesStatuses' || field === 'retStatuses') {
          const singularField = field === 'salesStatuses' ? 'salesStatus' : 'retStatus';
          next[singularField] = value[0] || '';
          if (field === activeStatuses) next.status = value[0] || '';
        }
        if (field === 'status') next[activeStatus] = value;
        return next;
      });
      if (!silent) toast.success('Saved');
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Save failed');
      return false;
    }
  };

  // ── Comments ───────────────────────────────────────────────────────────────
  const submitComment = async () => {
    if (!newComment.body)  { toast.error('Comment is required'); return; }
    try {
      const time = newComment.next_call_time || '10:00';
      // Convert local picker selection to UTC so the server stores and compares UTC correctly
      const nextCallAt = newComment.next_call_date
        ? new Date(`${newComment.next_call_date}T${time}:00`).toISOString().slice(0, 19)
        : null;
      await api.post(`/clients/${id}/comments`, {
        title: 'Call Log',
        body: newComment.body,
        pinned: newComment.pinned,
        nextCallAt,
      });
      toast.success(nextCallAt ? 'Comment saved & call scheduled' : 'Comment saved');
      setShowNewComment(false);
      setNewComment({ body: '', pinned: false, next_call_date: '', next_call_time: '' });
      loadComments();
      api.get(`/clients/${id}/comments`).then(({ data }) => {
        setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
      }).catch(() => {});
    } catch { toast.error('Failed to add comment'); }
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete(`/clients/${id}/comments/${cid}`);
      loadComments();
      api.get(`/clients/${id}/comments`).then(({ data }) => {
        setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
      }).catch(() => {});
      toast.success('Deleted');
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const openEditComment = (c) => {
    const dt = c.nextCallAt ? new Date(c.nextCallAt) : null;
    setEditCommentForm({
      title:          c.title || '',
      body:           c.body  || '',
      pinned:         c.pinned || false,
      next_call_date: dt ? dt.toISOString().slice(0, 10) : '',
      next_call_time: dt ? `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}` : '',
    });
    setEditingComment(c);
  };

  const submitEditComment = async () => {
    if (!editCommentForm.title) { toast.error('Topic is required'); return; }
    if (!editCommentForm.body)  { toast.error('Comment is required'); return; }
    try {
      const time = editCommentForm.next_call_time || '10:00';
      const nextCallAt = editCommentForm.next_call_date
        ? new Date(`${editCommentForm.next_call_date}T${time}:00`).toISOString().slice(0, 19)
        : null;
      await api.patch(`/clients/${id}/comments/${editingComment.id}`, {
        title:        editCommentForm.title,
        body:         editCommentForm.body,
        pinned:       editCommentForm.pinned,
        nextCallAt,
      });
      toast.success('Comment updated');
      setEditingComment(null);
      loadComments();
      // refresh pinned notes
      api.get(`/clients/${id}/comments`).then(({ data }) => {
        setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
      }).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  // ── Transactions (per-client) ──────────────────────────────────────────────
  const submitNewTxn = async () => {
    const amount = parseFloat(String(newTxn.amount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Amount required'); return; }
    try {
      const canUseAdminTxnControls = canUseTransactionAdminControls(userRole);
      await api.post('/transactions', {
        clientId: String(id),
        type: canUseAdminTxnControls ? newTxn.type : 'deposit',
        amount,
        currency: 'USD',
        status: canUseAdminTxnControls ? newTxn.status : 'completed',
        description: newTxn.description || null,
      });
      toast.success('Transaction created');
      setShowNewTxn(false);
      setNewTxn({ amount: '', type: 'deposit', currency: 'USD', status: 'completed', description: '' });
      loadClientTxns();
    } catch { toast.error('Failed to create transaction'); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className={`flex items-center justify-center h-screen ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('clientDetail.loading')}</div>
  );
  if (!client) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-4 text-center">
      <p role="alert" className="text-red-400">{t(`clientDetail.${loadError || 'loadFailed'}`)}</p>
      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={loadClient} className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700">
          {t('clientDetail.retry')}
        </button>
        <button onClick={() => navigate('/clients')} className={`px-4 py-2 rounded-lg border ${isDark ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
          {t('clientDetail.backToClients')}
        </button>
      </div>
    </div>
  );

  const TABS = ['comments', 'transactions', 'documents', 'personalInfo'];
  const TAB_LABELS = {
    comments: t('clientDetail.comments'),
    transactions: t('clientDetail.transactions'),
    documents: t('clientDetail.documents'),
    personalInfo: t('clientDetail.personalInfo'),
  };
  const FIELD_LABELS = {
    'Date of birth': t('clientDetail.dateOfBirth'),
    Gender: t('clientDetail.gender'),
    Phone: t('clientDetail.phone'),
    'Second Phone': t('clientDetail.secondPhone'),
    Email: t('clientDetail.email'),
    'Second Email': t('clientDetail.secondEmail'),
    Country: t('clientDetail.country'),
    Citizenship: t('clientDetail.citizenship'),
    Language: t('clientDetail.language'),
    City: t('clientDetail.city'),
    Address: t('clientDetail.address'),
    Affiliate: t('clientDetail.affiliate'),
    'Affiliate ID': t('clientDetail.affiliateId'),
    Source: t('clientDetail.source'),
    Referral: t('clientDetail.referral'),
    Funnel: t('clientDetail.funnel'),
    'Reg. IP': t('clientDetail.regIp'),
  };

  const age      = calcAge(client.birthdate || client.birth_date);
  const fullName = [client.firstName, client.lastName].filter(Boolean).join(' ') || '—';

  const dept = (client.department || '').toLowerCase();

  // Current user role from the backend auth context
  const currentUser  = getCurrentUser();
  const userRole     = currentUser.role || '';
  const canAssignClients = hasPermission(currentUser, 'Assign Clients');
  const canUseAdminTxnControls = canUseTransactionAdminControls(userRole);
  const _canEditSales = canEditSales(userRole);
  const _canEditRet   = canEditRetention(userRole);

  // Card/section shared styles
  const section = `rounded-xl border ${isDark ? 'bg-[#0f1729] border-gray-800/80' : 'bg-white border-gray-200'} shadow-sm`;
  const sectionHd = `px-4 py-2.5 border-b text-[11px] font-bold uppercase tracking-widest ${isDark ? 'border-gray-800/80 text-gray-500' : 'border-gray-100 text-gray-400'}`;
  const infoRow = `flex items-center justify-between gap-2 px-4 py-2 rounded-lg transition-colors cursor-default ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'}`;
  const infoLabel = `text-[11px] font-medium flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
  const infoVal   = `text-[12px] font-medium text-right truncate max-w-[160px] ${isDark ? 'text-gray-200' : 'text-gray-700'}`;

  // Avatar — initials + dept color
  const initials = [client.firstName, client.lastName].filter(Boolean).map(n => n[0]).join('').toUpperCase() || '?';
  const deptAvatarStyle = dept === 'retention'
    ? { background: 'linear-gradient(135deg,#0e7490,#06b6d4)', color: '#fff' }
    : { background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', color: '#fff' };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0e1a]' : 'bg-gray-100'}`}>
      {showCallMenu && (
        <div
          ref={callMenuRef}
          className={`fixed z-[100] w-[180px] overflow-hidden rounded-md border shadow-xl ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}
          style={{ top: callMenuPos.top, left: callMenuPos.left }}
        >
          {callDialOptions.map((option, index) => (
            <button
              key={`${option.prefix || 'none'}-${index}`}
              type="button"
              onClick={() => startTelephonyCall(option)}
              className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs ${isDark ? 'text-gray-200 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span>{option.label || option.name || option.prefix || 'MicroSIP - No prefix'}</span>
              {option.prefix && <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{option.prefix}</span>}
            </button>
          ))}
        </div>
      )}

      {/* â•â• 1. TOP HEADER BAR â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div className={`sticky top-0 z-20 px-4 py-3 border-b flex items-center justify-between gap-4 ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}
        style={{ boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.06)' }}>

        {/* Left: back + avatar + name block */}
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/clients')}
            className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/[0.07]' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
            <ArrowLeft size={17} />
          </button>

          {/* Name + meta */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className={`text-[15px] font-bold truncate leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {fullName}{age ? <span className={`ml-1 font-normal text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>({age})</span> : ''}
              </h1>
              {client.active
                ? <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" style={{ boxShadow: '0 0 5px rgba(74,222,128,0.8)' }} />
                    {t('clientDetail.active')}
                  </span>
                : <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }}>
                    {t('clientDetail.blocked')}
                  </span>
              }
            </div>
            <p className={`text-[11px] font-mono mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>#{client.id}</p>
          </div>
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {canAssignClients && (
            <button
              onClick={async () => {
                try {
                  const { data } = await api.get('/clients/assignment-options');
                  setTransferUsers(data.users || []);
                } catch {
                  setTransferUsers([]);
                }
                setShowTransfer(true);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
              style={{ borderColor: 'rgba(99,102,241,0.4)', color: isDark ? '#a5b4fc' : '#6366f1' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Users size={12} /> Transfer to agent
            </button>
          )}
          <button
            onClick={() => setShowNewComment(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
            style={{ borderColor: 'rgba(99,102,241,0.4)', color: isDark ? '#a5b4fc' : '#6366f1' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <Phone size={12} /> {t('clientDetail.addCallback')}
          </button>
          <button
            onClick={() => setShowChangeStatus(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all"
            style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(139,92,246,0.12))',
              border: '1px solid rgba(99,102,241,0.4)',
              color: '#a5b4fc',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 14px rgba(99,102,241,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            <ChevronDown size={12} /> {t('clientDetail.changeStatus')}
          </button>
          <button onClick={loadClient}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/[0.07]' : 'text-gray-500 hover:bg-gray-100'}`}
            title={t('clientDetail.refresh')}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* â•â• 3. INFO GRID + TABS â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div className="p-4 space-y-4">

        <div className="grid grid-cols-3 gap-4">

          {/* ── Col 1: Personal Information ── */}
          <div className={section} style={{ position: 'relative' }} ref={fieldsConfigRef}>
            <div className={`${sectionHd} flex items-center justify-between`}>
              <span>{t('clientDetail.personalInformation')}</span>
              <button
                onClick={() => setShowFieldsConfig(v => !v)}
                title={t('clientDetail.configureVisibleFields')}
                className={`p-0.5 rounded transition-colors ${isDark ? 'text-gray-600 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </button>
            </div>

            {/* Fields config dropdown */}
            {showFieldsConfig && (
              <div
                className={`absolute right-0 top-8 z-30 w-52 rounded-lg border shadow-xl p-2 ${isDark ? 'bg-[#0f1729] border-gray-700' : 'bg-white border-gray-200'}`}
                style={{ top: '2rem' }}
              >
                <p className={`text-xs font-semibold px-1 pb-1 mb-1 border-b ${isDark ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-100'}`}>{t('clientDetail.visibleFields')}</p>
                {PI_FIELDS.map(f => (
                  <label key={f} className={`flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer text-xs ${isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}>
                    <input type="checkbox" checked={visibleFields.has(f)} onChange={() => toggleFieldVisibility(f)} className="accent-purple-500 w-3 h-3" />
                    {FIELD_LABELS[f] || f}
                  </label>
                ))}
              </div>
            )}

            <div className="py-1 overflow-y-auto" style={{ maxHeight: 320 }}>
              {[
                ['Date of birth', client.birthdate || client.birth_date],
                ['Gender',        client.gender],
                ['Phone',         client.phone],
                ['Second Phone',  client.phone2],
                ['Email',         client.email],
                ['Second Email',  client.email2],
                ['Country',       client.country],
                ['Citizenship',   client.citizenship],
                ['Language',      client.language],
                ['City',          client.city],
                ['Address',       client.address],
                ['Affiliate',     client.affiliate],
                ['Affiliate ID',  client.extId || client.affiliateId],
                ['Source',        client.source],
                ['Referral',      client.referral],
                ['Funnel',        client.funnel],
                ['Reg. IP',       client.registrationIp],
              ].filter(([label]) => visibleFields.has(label)).map(([label, val]) => (
                <div key={label} className={infoRow}>
                  <span className={infoLabel}>{FIELD_LABELS[label] || label}</span>
                  <span className={infoVal} title={val || '—'}>
                    {val
                      ? <span className="inline-flex items-center gap-1.5">
                          <span>{val}</span>
                          {label === 'Phone' && (
                            <span className="inline-flex">
                              <button
                                ref={callButtonRef}
                                onClick={toggleCallMenu}
                                title={t('clientDetail.callClient')}
                                className="inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors"
                                style={{ background: 'rgba(34,197,94,0.12)', color: isDark ? '#86efac' : '#16a34a' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.25)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.12)'; }}>
                                <Phone size={10} />
                              </button>
                            </span>
                          )}
                        </span>
                      : <span style={{ color: isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db' }}>—</span>
                    }
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Col 2: Acquisition Status ── */}
          <div className={section}>
            <div className={sectionHd}>{t('clientDetail.acquisitionStatus')}</div>
            <div className="p-3">
              <DeptStatusCard
                deptLabel={t('clientDetail.sales')}
                deptColor={isDark ? 'text-blue-400' : 'text-blue-600'}
                accentBorder={isDark ? 'border-blue-500/60' : 'border-blue-400'}
                accentBg={isDark ? 'bg-blue-950/20' : 'bg-blue-50'}
                isActive={dept !== 'retention'}
                statuses={client.salesStatuses?.length ? client.salesStatuses : [client.salesStatus || (dept !== 'retention' ? client.status : '')].filter(Boolean)}
                agent={client.salesAgent || (dept !== 'retention' ? client.agent : null)}
                desk={client.salesDesk  || (dept !== 'retention' ? client.desk   : null)}
                team={client.salesTeam  || (dept !== 'retention' ? client.team   : null)}
                isDark={isDark}
                t={t}
              />
              <DeptStatusCard
                deptLabel={t('clientDetail.retention')}
                deptColor={isDark ? 'text-cyan-400' : 'text-cyan-600'}
                accentBorder={isDark ? 'border-cyan-500/60' : 'border-cyan-400'}
                accentBg={isDark ? 'bg-cyan-950/20' : 'bg-cyan-50'}
                isActive={dept === 'retention'}
                statuses={client.retStatuses?.length ? client.retStatuses : [client.retStatus || (dept === 'retention' ? client.status : '')].filter(Boolean)}
                agent={client.retAgent || (dept === 'retention' ? client.agent : null)}
                desk={client.retDesk   || (dept === 'retention' ? client.desk   : null)}
                team={client.retTeam   || (dept === 'retention' ? client.team   : null)}
                isDark={isDark}
                t={t}
              />
            </div>
          </div>

          {/* ── Col 3: Pinned Notes ── */}
          <div className={section}>
            <div className={sectionHd}>{t('clientDetail.pinnedNotes')}</div>
            <div className="px-3 py-1 overflow-y-auto" style={{ maxHeight: 320 }}>
              {pinnedComments.length === 0 ? (
                <p className={`text-xs text-center py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{t('clientDetail.noPinnedNotes')}</p>
              ) : pinnedComments.map(c => (
                <div key={c.id} className={`py-2 border-b last:border-0 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                  <div className="flex items-start gap-1">
                    <Pin size={10} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className={`text-xs font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{c.title}</p>
                      <p className={`text-xs mt-0.5 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{c.body}</p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{fmtDT(c.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── TABS ── */}
        <div className={`rounded-xl border shadow-sm ${isDark ? 'bg-[#0f1729] border-gray-800/80' : 'bg-white border-gray-200'}`}>
          <div className={`flex border-b overflow-x-auto ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            {TABS.map(t => <Tab key={t} label={TAB_LABELS[t]} active={activeTab === t} onClick={() => setActiveTab(t)} isDark={isDark} />)}
          </div>

          <div className="p-5">

            {/* â•â•â•â• COMMENTS â•â•â•â• */}
            {activeTab === 'comments' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{t('clientDetail.comments')}</h3>
                  <button onClick={() => setShowNewComment(true)} className={cls.btnPrimary + ' flex items-center gap-1'}>
                    <Plus size={14} /> {t('clientDetail.newComment')}
                  </button>
                </div>
                {comments.length === 0 ? (
                  <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('clientDetail.noCommentsYet')}</p>
                ) : (
                  <div className="space-y-3">
                    {comments.map(c => (
                      <div key={c.id} className={`p-4 rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-gray-50 border-gray-200'} ${c.pinned ? 'border-l-4 border-l-yellow-500' : ''}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {/* Header row: pin + topic + status badge */}
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {c.pinned && <Pin size={12} className="text-yellow-500 flex-shrink-0" />}
                              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.title}</span>
                            </div>
                            {/* Comment body */}
                            <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{c.body}</p>
                            {/* Next call */}
                            {c.nextCallAt && (
                              <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                <Phone size={11} />
                                <span>Next call: {fmtDate(c.nextCallAt)}</span>
                              </div>
                            )}
                            {/* Footer: author + date */}
                            <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.author} · {fmtDate(c.createdAt)}</p>
                          </div>
                          {/* Action buttons — role-gated */}
                          <div className="flex gap-0.5 flex-shrink-0">
                            {canEditComment(userRole) && (
                              <button onClick={() => openEditComment(c)}
                                className={`p-1 rounded transition-colors ${isDark ? 'text-gray-600 hover:text-gray-300 hover:bg-gray-700/40' : 'text-gray-300 hover:text-gray-600 hover:bg-gray-100'}`}
                                title="Edit comment">
                                <Pencil size={13} />
                              </button>
                            )}
                            {canDeleteComment(userRole) && (
                              <button onClick={() => deleteComment(c.id)}
                                className="p-1 rounded text-red-500/40 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                                title="Delete comment">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* â•â•â•â• TRANSACTIONS â•â•â•â• */}
            {activeTab === 'transactions' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Transactions</h3>
                  <button onClick={() => setShowNewTxn(true)} className={cls.btnPrimary + ' flex items-center gap-1'}>
                    <Plus size={14} /> New Transaction
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
                        {['ID', 'Type', 'Amount USD', 'Status', 'Description', 'Date'].map(h => (
                          <th key={h} className="text-left px-3 py-2 font-medium text-xs whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {clientTxns.length === 0 ? (
                        <tr><td colSpan={6} className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No transactions</td></tr>
                      ) : clientTxns.map(t => (
                        <tr key={t.id} className={`border-b ${isDark ? 'border-gray-800/50 hover:bg-[#1a1f3a]/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <td className={`px-3 py-2.5 font-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.id.slice(0, 8)}…</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.type === 'deposit' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {t.type}
                            </span>
                          </td>
                          <td className={`px-3 py-2.5 font-semibold ${t.type === 'deposit' ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`}>
                            {t.type === 'deposit' ? '+' : '-'}${t.amount?.toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {t.status}
                            </span>
                          </td>
                          <td className={`px-3 py-2.5 text-xs max-w-[160px] truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`} title={t.description}>{t.description || '—'}</td>
                          <td className={`px-3 py-2.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{fmtDate(t.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* â•â•â•â• DOCUMENTS â•â•â•â• */}
            {activeTab === 'documents' && (
              <div>
                {/* sub-tabs */}
                <div className={`flex gap-1 mb-5 p-1 rounded-lg w-fit ${isDark ? 'bg-[#0f1729]' : 'bg-gray-100'}`}>
                  {[{id:'verification',label:'Verification'},{id:'files',label:'Files'},{id:'case',label:'Case'},{id:'invoices',label:'Invoices'}].map(t => (
                    <button key={t.id} onClick={() => setDocSubTab(t.id)}
                      className={`px-4 py-1.5 text-sm rounded-md transition-colors ${docSubTab === t.id ? 'bg-purple-600 text-white' : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {docSubTab === 'verification' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Client Documents</h3>
                      <button onClick={loadClientDocs} className={`${cls.btn} flex items-center gap-1 text-xs py-1`}><RefreshCw size={12} /></button>
                    </div>
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {DOC_TYPES.map(docType => {
                          // Top slot = agent-uploaded, OR client-uploaded that has been approved
                          // (declined/pending client uploads stay in the "Uploaded by client" list below)
                          const existing = clientDocs.filter(d => d.docType === docType && (
                            (d.uploaderRole || 'agent') === 'agent' || d.status === 'approved'
                          ));
                          const latest = existing[0];
                          const statusColors = {
                            approved: 'border-green-500/50 bg-green-500/5',
                            declined: 'border-red-500/50 bg-red-500/5',
                            pending: 'border-yellow-500/50 bg-yellow-500/5',
                          };
                          const statusText = {
                            approved: <span className="text-green-400 text-xs font-medium">✓ Approved</span>,
                            declined: <span className="text-red-400 text-xs font-medium">✗ Declined</span>,
                            pending: <span className="text-yellow-400 text-xs font-medium">Pending</span>,
                          };
                          return (
                            <div key={docType}
                              className={`p-4 rounded-lg border-2 text-center transition-colors ${latest ? statusColors[latest.status] : (isDark ? 'border-gray-700 hover:border-purple-600 bg-[#0f1729]' : 'border-gray-300 hover:border-purple-400 bg-gray-50')} ${isDark ? '' : ''}`}>
                              <FileText size={24} className={`mx-auto mb-2 ${latest ? (latest.status === 'approved' ? 'text-green-400' : latest.status === 'declined' ? 'text-red-400' : 'text-yellow-400') : (isDark ? 'text-gray-600' : 'text-gray-400')}`} />
                              <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{docType}</p>
                              {latest ? (
                                <div className="space-y-1.5">
                                  {statusText[latest.status]}
                                  <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{latest.fileName}</p>
                                  <div className="flex justify-center gap-1 pt-1">
                                    {latest.status !== 'approved' && (
                                      <button onClick={() => changeDocStatus(latest.id, 'approved')}
                                        className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 hover:bg-green-500/30">Approve</button>
                                    )}
                                    {latest.status !== 'declined' && (
                                      <button onClick={() => changeDocStatus(latest.id, 'declined')}
                                        className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30">Decline</button>
                                    )}
                                    <button onClick={() => setPreviewDoc(latest)}
                                      className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                                      title="Preview document">View</button>
                                    <button onClick={() => deleteClientDoc(latest.id)}
                                      className="px-2 py-0.5 text-xs rounded bg-gray-500/20 text-gray-400 hover:bg-gray-500/30">Del</button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <button
                                    onClick={() => { setUploadDocType(docType); docFileRef.current?.click(); }}
                                    disabled={uploadingDoc}
                                    className={`text-xs px-3 py-1 rounded-md border transition-colors ${isDark ? 'border-gray-600 text-gray-400 hover:border-purple-500 hover:text-purple-400' : 'border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-500'}`}>
                                    <Upload size={11} className="inline mr-1" />Upload
                                  </button>
                                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>Max 10 MB</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Client-uploaded documents — split into Pending and Declined sections */}
                    {(() => {
                      const clientUploads = clientDocs.filter(d => d.uploaderRole === 'client' && DOC_TYPES.includes(d.docType));
                      const pendingDocs = clientUploads.filter(d => d.status === 'pending');
                      const declinedDocs = clientUploads.filter(d => d.status === 'declined');
                      const statusBadge = {
                        approved: <span className="text-green-400 text-xs font-medium">✓ Approved</span>,
                        declined: <span className="text-red-400 text-xs font-medium">✗ Declined</span>,
                        pending: <span className="text-yellow-400 text-xs font-medium">Pending</span>,
                      };
                      const renderRow = (doc) => (
                        <div key={doc.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                          <FileText size={18} className={`flex-shrink-0 ${doc.status === 'approved' ? 'text-green-400' : doc.status === 'declined' ? 'text-red-400' : 'text-yellow-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              {doc.fileName} <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'} font-normal`}>({doc.docType})</span>
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}
                            </p>
                          </div>
                          {statusBadge[doc.status]}
                          {doc.status !== 'approved' && (
                            <button onClick={() => changeDocStatus(doc.id, 'approved')}
                              className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 flex-shrink-0">Approve</button>
                          )}
                          {doc.status !== 'declined' && (
                            <button onClick={() => changeDocStatus(doc.id, 'declined')}
                              className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-shrink-0">Decline</button>
                          )}
                          <button onClick={() => setPreviewDoc(doc)}
                            className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 flex-shrink-0"
                            title="Preview document">View</button>
                          <button onClick={() => deleteClientDoc(doc.id)}
                            className="px-2 py-0.5 text-xs rounded bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 flex-shrink-0">Del</button>
                        </div>
                      );
                      return (
                        <>
                          {pendingDocs.length > 0 && (
                            <div className="mt-8">
                              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                Uploaded by client <span className={`text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· awaiting review</span>
                              </h3>
                              <div className="space-y-2">{pendingDocs.map(renderRow)}</div>
                            </div>
                          )}
                          {declinedDocs.length > 0 && (
                            <div className="mt-8">
                              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                                Declined documents <span className={`text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· {declinedDocs.length}</span>
                              </h3>
                              <div className="space-y-2">{declinedDocs.map(renderRow)}</div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {/* hidden file input */}
                    <input ref={docFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                      onChange={e => { const f = e.target.files[0]; if (f && uploadDocType) handleDocUpload(f, uploadDocType); e.target.value = ''; }} />
                  </div>
                )}

                {docSubTab === 'files' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Files</h3>
                      <div className="flex gap-2">
                        <button onClick={() => filesFileRef.current?.click()} disabled={uploadingFiles}
                          className={`${cls.btnPrimary} flex items-center gap-1 text-xs py-1.5 px-3`}>
                          <Upload size={12} />{uploadingFiles ? 'Uploading…' : 'Upload File'}
                        </button>
                      </div>
                    </div>
                    <input ref={filesFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.zip" className="hidden"
                      onChange={e => { const f = e.target.files[0]; if (f) handleFilesUpload(f); e.target.value = ''; }} />
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (() => {
                      const files = clientDocs.filter(d => d.docType === 'file');
                      return files.length === 0 ? (
                        <p className={`text-sm text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No files yet</p>
                      ) : (
                        <div className="space-y-2">
                          {files.map(doc => (
                            <div key={doc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                              <FileText size={18} className="text-blue-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{doc.fileName}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <button onClick={() => setPreviewDoc(doc)} className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 flex-shrink-0" title="Preview document">View</button>
                              <button onClick={() => deleteClientDoc(doc.id)}
                                className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-shrink-0">Del</button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {docSubTab === 'case' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Case Files</h3>
                      <button onClick={loadClientDocs} className={`${cls.btn} flex items-center gap-1 text-xs py-1`}><RefreshCw size={12} /></button>
                    </div>
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (() => {
                      const caseFiles = clientDocs.filter(d => d.docType === 'case_file');
                      return caseFiles.length === 0 ? (
                        <p className={`text-sm text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No case files yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {caseFiles.map(doc => (
                            <div key={doc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                              <FileText size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{doc.fileName}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <button onClick={() => setPreviewDoc(doc)}
                                className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                                title="Preview document">View</button>
                              
                              <button onClick={() => deleteClientDoc(doc.id)}
                                className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30">Del</button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {docSubTab === 'invoices' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Invoices</h3>
                      <div className="flex gap-2">
                        <button onClick={() => invoiceFileRef.current?.click()} disabled={uploadingInvoice}
                          className={`${cls.btnPrimary} flex items-center gap-1 text-xs py-1.5 px-3`}>
                          <Upload size={12} />{uploadingInvoice ? 'Uploading…' : 'Upload'}
                        </button>
                      </div>
                    </div>
                    <input ref={invoiceFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" className="hidden"
                      onChange={e => { const f = e.target.files[0]; if (f) handleInvoiceUpload(f); e.target.value = ''; }} />
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (() => {
                      const invoices = clientDocs.filter(d => d.docType === 'invoice');
                      return invoices.length === 0 ? (
                        <p className={`text-sm text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No invoices yet</p>
                      ) : (
                        <div className="space-y-2">
                          {invoices.map(doc => (
                            <div key={doc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                              <FileText size={18} className="text-purple-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{doc.fileName}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <button onClick={() => setPreviewDoc(doc)} className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 flex-shrink-0" title="Preview document">View</button>
                              <button onClick={() => deleteClientDoc(doc.id)}
                                className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-shrink-0">Del</button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}


            {/* UPLOAD PREVIEW (delete / save as template / publish to portal) */}
            {previewDoc && (
              <UploadPreviewModal
                doc={previewDoc}
                isDark={isDark}
                onClose={() => setPreviewDoc(null)}
                onDeleted={loadClientDocs}
                onTemplated={loadClientDocs}
                onStatusChanged={loadClientDocs}
              />
            )}

            {activeTab === 'personalInfo' && (() => {
              // Compact inline-editable field — designed for 2-col grid
              const PIField = ({ label, field, value, type = 'text', options, wide }) => {
                const [editing, setEditing] = React.useState(false);
                const [val, setVal] = React.useState(value ?? '');
                React.useEffect(() => { setVal(value ?? ''); }, [value]);
                const save = async () => { await handleFieldSave(field, val); setEditing(false); };
                const cancel = () => { setVal(value ?? ''); setEditing(false); };
                return (
                  <div className={`group rounded-lg px-3 py-2.5 transition-colors ${wide ? 'col-span-2' : ''} ${isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-50'}`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
                    {editing ? (
                      <div className="flex items-center gap-1">
                        {options ? (
                          <select value={val} onChange={e => setVal(e.target.value)} autoFocus
                            className={`flex-1 text-sm px-2 py-1 rounded-md border focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                            <option value="">—</option>
                            {options.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input type={type} value={val} onChange={e => setVal(e.target.value)} autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
                            className={`flex-1 text-sm px-2 py-1 rounded-md border focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                        )}
                        <button onClick={save} className="p-1 rounded text-green-400 hover:bg-green-500/10 flex-shrink-0"><Check size={13} /></button>
                        <button onClick={cancel} className={`p-1 rounded flex-shrink-0 ${isDark ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}><X size={13} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-1 min-w-0">
                        <span className={`text-sm truncate ${value ? (isDark ? 'text-gray-200' : 'text-gray-800') : (isDark ? 'text-gray-700' : 'text-gray-300')}`}>
                          {value || '—'}
                        </span>
                        <button onClick={() => setEditing(true)}
                          className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                          <Pencil size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-800/80' : 'border-gray-200'}`}>
                  {/* 2-column compact grid */}
                  <div className="grid grid-cols-2 divide-x divide-y" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}>
                    <PIField label="Date of birth"  field="birthdate"      value={client.birthdate || client.birth_date} type="date" />
                    <PIField label="Gender"          field="gender"         value={client.gender}    options={['Male','Female','Other']} />
                    <PIField label="Phone"           field="phone"          value={client.phone} />
                    <PIField label="Second Phone"    field="phone2"         value={client.phone2} />
                    <PIField label="Email"           field="email"          value={client.email}     type="email" />
                    <PIField label="Second Email"    field="email2"         value={client.email2}    type="email" />
                    <PIField label="Country"         field="country"        value={client.country} />
                    <PIField label="Citizenship"     field="citizenship"    value={client.citizenship} />
                    <PIField label="Language"        field="language"       value={client.language} />
                    <PIField label="City"            field="city"           value={client.city} />
                    <PIField label="Address"         field="address"        value={client.address} />
                    <PIField label="Affiliate"       field="affiliate"      value={client.affiliate} />
                    <PIField label="Affiliate ID"    field="extId"          value={client.extId || client.affiliateId} />
                    <PIField label="Source"          field="source"         value={client.source} />
                    <PIField label="Referral"        field="referral"       value={client.referral} />
                    <PIField label="Funnel"          field="funnel"         value={client.funnel} />
                    <PIField label="Reg. IP"         field="registrationIp" value={client.registrationIp} />
                  </div>
                </div>
              );
            })()}

          </div>
        </div>
      </div>

      {/* â•â• MODALS â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}

      {/* New Comment */}
      {showNewComment && (
        <Modal title="New Call Log" onClose={() => { setShowNewComment(false); setNewComment({ body: '', pinned: false, status: '', next_call_date: '', next_call_time: '' }); }} isDark={isDark} maxW="max-w-lg">
          <div className="space-y-4">
            {/* Next Call — optional */}
            <div>
              <label className={`${cls.label} flex items-center gap-1`}>
                <CalendarDays size={12} />
                Next Call (optional)
              </label>
              <DateTimePicker
                date={newComment.next_call_date}
                time={newComment.next_call_time}
                onChangeDT={(d, t) => setNewComment(p => ({ ...p, next_call_date: d, next_call_time: t }))}
                isDark={isDark}
                placeholder="Pick date & time"
              />
            </div>

            {/* Comment body */}
            <div>
              <label className={cls.label}>Comment *</label>
              <textarea
                value={newComment.body}
                onChange={e => setNewComment(p => ({ ...p, body: e.target.value }))}
                className={cls.input}
                rows={4}
                placeholder="What was discussed, outcome, next steps…"
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newComment.pinned} onChange={e => setNewComment(p => ({ ...p, pinned: e.target.checked }))} className="w-4 h-4 accent-purple-500" />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pin</span>
              </label>
              <button onClick={submitComment} className={cls.btnPrimary}>Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Comment */}
      {editingComment && (
        <Modal title="Edit Comment" onClose={() => setEditingComment(null)} isDark={isDark} maxW="max-w-lg">
          <div className="space-y-4">
            <div>
              <label className={cls.label}>Topic *</label>
              <input
                value={editCommentForm.title}
                onChange={e => setEditCommentForm(p => ({ ...p, title: e.target.value }))}
                className={cls.input}
                autoFocus
              />
            </div>
            <div>
              <label className={`${cls.label} flex items-center gap-1`}>
                <CalendarDays size={12} /> Next Call
              </label>
              <DateTimePicker
                date={editCommentForm.next_call_date}
                time={editCommentForm.next_call_time}
                onChangeDT={(d, t) => setEditCommentForm(p => ({ ...p, next_call_date: d, next_call_time: t }))}
                isDark={isDark}
                placeholder="Pick date & time"
              />
            </div>
            <div>
              <label className={cls.label}>Comment *</label>
              <textarea
                value={editCommentForm.body}
                onChange={e => setEditCommentForm(p => ({ ...p, body: e.target.value }))}
                className={cls.input}
                rows={4}
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editCommentForm.pinned}
                  onChange={e => setEditCommentForm(p => ({ ...p, pinned: e.target.checked }))}
                  className="w-4 h-4 accent-purple-500" />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pin</span>
              </label>
              <div className="flex gap-2">
                <button onClick={() => setEditingComment(null)} className={cls.btn}>Cancel</button>
                <button onClick={submitEditComment} className={cls.btnPrimary}>Save</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* New Transaction */}
      {showNewTxn && (
        <Modal title="New Transaction" onClose={() => setShowNewTxn(false)} isDark={isDark}>
          <div className="space-y-3">
            <div>
              <label className={cls.label}>Amount USD *</label>
              <input type="text" inputMode="decimal" value={newTxn.amount} onChange={e => setNewTxn(p => ({ ...p, amount: e.target.value }))} className={cls.input} placeholder="0.00" />
            </div>
            {canUseAdminTxnControls && (
              <div>
                <label className={cls.label}>Type *</label>
                <select value={newTxn.type} onChange={e => setNewTxn(p => ({ ...p, type: e.target.value }))} className={cls.input}>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdraw</option>
                </select>
              </div>
            )}
            <div>
              <label className={cls.label}>Description</label>
              <input value={newTxn.description} onChange={e => setNewTxn(p => ({ ...p, description: e.target.value }))} className={cls.input} placeholder="e.g. Card payment, Crypto transfer…" />
            </div>
            {canUseAdminTxnControls && (
              <div>
                <label className={cls.label}>Status</label>
                <select value={newTxn.status} onChange={e => setNewTxn(p => ({ ...p, status: e.target.value }))} className={cls.input}>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={submitNewTxn} className={cls.btnPrimary}>Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Change Status */}
      {showChangeStatus && (
        <ChangeStatusModal
          client={client}
          statusOptions={statusOptions}
          isDark={isDark}
          onSave={handleFieldSave}
          onClose={() => setShowChangeStatus(false)}
          userRole={userRole}
        />
      )}

      {/* Transfer to another agent */}
      {showTransfer && (
        <TransferAgentModal
          client={client}
          clientId={id}
          users={transferUsers}
          statusOptions={statusOptions}
          isDark={isDark}
          userRole={userRole}
          onClose={() => setShowTransfer(false)}
          onTransferred={loadClient}
        />
      )}
    </div>
  );
}
